import { useState, useCallback, useEffect } from "react";
import { OperationType } from "relay-runtime";

export type FetchFunction<Q extends OperationType> = (args: {
	first: number;
	after: string | null;
	filter: Q["variables"];
}) => Promise<Q["response"] | null | undefined>;

export type NodeExtractor<Q extends OperationType, T> = (response: Q["response"]) => {
	nodes: T[];
	pageInfo: { endCursor: string | null | undefined; hasNextPage: boolean };
};

type UsePagedPaginationProps<Q extends OperationType, T> = {
	fetchFn: FetchFunction<Q>;
	nodeExtractor: NodeExtractor<Q, T>;
	initialFilter: Q["variables"];
	pageSize?: number;
};

export const usePagedPagination = <Q extends OperationType, T>({
	fetchFn,
	nodeExtractor,
	initialFilter,
	pageSize = 10,
}: UsePagedPaginationProps<Q, T>) => {
	const [filter, setFilter] = useState<Q["variables"]>(initialFilter);
	const [pages, setPages] = useState<Record<number, T[]>>({});
	const [currentPage, setCurrentPage] = useState(0);
	const [endCursor, setEndCursor] = useState<string | null>(null);
	const [hasNext, setHasNext] = useState(true);
	const [loading, setLoading] = useState(false);
	const fetchPage = useCallback(
		async (
			after: string | null = null,
			isNewSearch: boolean = false,
			pageToSet: number = 0,
		) => {
			setLoading(true);
			try {
				const response = await fetchFn({
					first: pageSize,
					after,
					filter,
				});

				if (!response) return;

				const { nodes, pageInfo } = nodeExtractor(response);

				setPages((prev) =>
					isNewSearch ? { [pageToSet]: nodes } : { ...prev, [pageToSet]: nodes },
				);

				setEndCursor(pageInfo.endCursor ?? null);
				setHasNext(pageInfo.hasNextPage);
				setCurrentPage(pageToSet);
			} finally {
				setLoading(false);
			}
		},
		[filter],
	);

	useEffect(() => {
		const timer = setTimeout(() => fetchPage(null, true, 0), 300);
		return () => clearTimeout(timer);
	}, [filter]);

	const handleNext = () => {
		const nextPage = currentPage + 1;
		if (pages[nextPage]) {
			return setCurrentPage(nextPage);
		}

		if (hasNext) {
			return fetchPage(endCursor, false, nextPage);
		}
	};

	const handlePrev = () => {
		if (currentPage > 0) setCurrentPage(currentPage - 1);
	};

	return {
		currentItems: pages[currentPage] || [],
		currentPage,
		loading,
		hasNext: hasNext || !!pages[currentPage + 1],
		handleNext,
		handlePrev,
		filter,
		setFilter,
		refetch: () => fetchPage(null, true, 0),
	};
};
